import { Banner, Button, PinPad, Tile, TileGrid } from "@omony/ui";
import { useMutation } from "@tanstack/react-query";
import { UserRound } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { api, ApiError, errorMessageId } from "../../shared/api.js";
import { queryClient, refreshSession } from "../../shared/query.js";
import styles from "../console.module.css";
import type { DeviceInfo } from "../data.js";

/**
 * Gesperrter Kiosk (ARCHITEKTUR §10.5): keine Patientendaten, nur Mitarbeitenden-Kacheln und PIN-Pad.
 * Nach 5 Fehlversuchen sperrt der Server die PIN (5 min, verdoppelnd).
 */
export function LockScreen({
  device,
  onUnlocked,
  onCancel,
}: {
  device: DeviceInfo;
  onUnlocked: (pin: string) => void;
  onCancel?: () => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const [staffId, setStaffId] = useState<string | null>(
    device.staff.length === 1 ? device.staff[0]!.id : null,
  );
  const [pin, setPin] = useState("");
  const m = useMutation({
    mutationFn: (p: string) =>
      api<{ csrfToken: string }>("/device/unlock", {
        method: "POST",
        body: { staffMemberId: staffId, pin: p },
      }),
    onSuccess: async (_r, p) => {
      // Erst die PIN merken (für den PIN-Wechsel), dann die Sitzung neu laden – sonst erscheint
      // der Wechsel-Bildschirm, bevor die PIN bekannt ist
      onUnlocked(p);
      queryClient.removeQueries({ queryKey: ["case"] });
      await refreshSession();
      await queryClient.invalidateQueries({ queryKey: ["device"] });
    },
    onError: () => setPin(""),
  });
  const person = device.staff.find((s) => s.id === staffId);
  const err = m.error instanceof ApiError ? m.error : null;
  const errorText = err
    ? err.code === "pin_invalid" && typeof err.extra.remaining === "number"
      ? t("console.lock.pinInvalidRemaining", { count: err.extra.remaining })
      : err.code === "pin_locked" && typeof err.extra.until === "string"
        ? t("console.lock.pinLockedUntil", { time: intl.formatTime(err.extra.until) })
        : t(errorMessageId(err))
    : null;

  return (
    <main id="main" className={styles.lockPage}>
      <div className={styles.lockCard}>
        <div className={styles.lockHead}>
          <span className={styles.eyebrow}>{t("console.lock.eyebrow")}</span>
          <h1 className={styles.lockTitle}>{device.pharmacy.name}</h1>
          <p className={styles.muted}>
            {person ? t("console.lock.enterPin", { name: person.displayName }) : t("console.lock.whoAreYou")}
          </p>
        </div>
        {!person ? (
          device.staff.length ? (
            <TileGrid label={t("console.lock.staffLabel")} min={200}>
              {device.staff.map((s) => (
                <Tile
                  key={s.id}
                  title={s.displayName}
                  icon={<UserRound />}
                  onPress={() => setStaffId(s.id)}
                />
              ))}
            </TileGrid>
          ) : (
            <Banner tone="warning" live={false}>
              {t("console.lock.noStaff")}
            </Banner>
          )
        ) : (
          <div className={styles.pinArea}>
            {errorText ? <Banner tone="danger">{errorText}</Banner> : null}
            <PinPad
              label={t("console.lock.pinLabel")}
              value={pin}
              onChange={setPin}
              onSubmit={(p) => m.mutate(p)}
              isDisabled={m.isPending}
            />
            {device.staff.length > 1 ? (
              <Button
                variant="quiet"
                onPress={() => {
                  setStaffId(null);
                  setPin("");
                  m.reset();
                }}
              >
                {t("console.lock.otherPerson")}
              </Button>
            ) : null}
          </div>
        )}
        {onCancel ? (
          <Button variant="secondary" onPress={onCancel}>
            {t("console.lock.backToPatient")}
          </Button>
        ) : null}
        <p className={styles.deviceName}>{t("console.lock.device", { name: device.device.name })}</p>
      </div>
    </main>
  );
}
